import { useCallback, useState } from "react";

import { PlusCircleOutlined } from "@ant-design/icons";
import { Empty, Form, Input, Select, Typography } from "antd";
import { FormListProps } from "antd/lib/form";
import { useDispatch } from "react-redux";

import { RVCollapse, RVButton, RVFormFloatingLabelWrap, RVLearnMoreLink } from "@regal-voice/ui";
import { RVEditableFormItemPanelHeader } from "Components/elements/RVCollapse/Shared/Components/RVEditableFormItemPanelHeader/RVEditableFormItemPanelHeader";
import { generateFormItemRequiredRules } from "Components/FormItems/Shared/Validators/InputValidators/InputValidators";
import { setIfShouldDropOutboundEdges } from "Services/state/journeys/JourneyStateSlice";
import { DecisionAiVariant } from "Types/Journey";
import { OpenAIModels } from "Types/OpenAi";

import { DecisionAIFormItemKeys, DecisionAIVariantFormItemKeys } from "../constants";
import { DecisionAILexicalInput } from "./Components/DecisionAILexicalInput/DecisionAILexicalInput";

import sharedStyles from "../SharedDemoStyles.module.scss";
import styles from "./DecisionAIFormItems.module.scss";

const AiDecision = {
    SENTIMENT_ANALYSIS: "Sentiment Analysis",
    LANGUAGE_DETECTION: "Language Detection",
    INTENT_CLASSIFICATION: "Intent Classification",
    YES_OR_NO: "Yes / No",
    CUSTOM: "Custom",
} as const;
type AiDecisionType = (typeof AiDecision)[keyof typeof AiDecision];
const AI_DECISION_TYPES: AiDecisionType[] = [
    AiDecision.SENTIMENT_ANALYSIS,
    AiDecision.LANGUAGE_DETECTION,
    AiDecision.INTENT_CLASSIFICATION,
    AiDecision.YES_OR_NO,
    AiDecision.CUSTOM,
];
const DECISION_TYPE_OPTIONS = AI_DECISION_TYPES.map((decision) => ({
    label: decision,
    value: decision,
}));

const MODEL_OPTIONS = [
    {
        label: "OpenAI 4o Mini",
        value: OpenAIModels.GPT_4_MINI,
    },
];

const DEFAULT_FORM_VALUES_PER_DECISION_TYPE = {
    [AiDecision.SENTIMENT_ANALYSIS]: {
        [DecisionAIFormItemKeys.name]: "New Lead Sentiment",
        [DecisionAIFormItemKeys.prompt]: `**TEMPLATE PROMPT** (modify prompt and output details for your use case, and delete this sentence)

### Background
You are a sentiment classification AI. Your task is to classify the sentiment of a contact’s SMS response from a campaign.

#Input
- Contact’s response: "{{event.properties.content}}"

# Task Steps
1. Choose only one of the following sentiment options.`,
        [DecisionAIFormItemKeys.variants]: [
            {
                id: 1,
                name: "Positive Sentiment",
                description:
                    "Contact's response indicates positive interest towards scheduling a call, and additional inquiry on the product.",
            },
            {
                id: 2,
                name: "Neutral Sentiment",
                description: `Contact's response does not give clear indication on intent, unrelated to the outreach, or too few words to detect any sentiment. E.g. “not sure".`,
            },
            {
                id: 3,
                name: "Negative Sentiment",
                description: `Contact's response indicates negative sentiment towards the outreach campaign, including "stop" type of unsubscribe texts.`,
            },
        ],
    },
    [AiDecision.INTENT_CLASSIFICATION]: {
        [DecisionAIFormItemKeys.name]: "Inbound Email Triage",
        [DecisionAIFormItemKeys.prompt]: `**TEMPLATE PROMPT** (modify prompt and output details for your use case, and delete this sentence)

### Background
You are an intent classification AI. Your task is to analyze the content of a customer email and identify its primary intent.

#Input
- Contact’s email content: "{{event.properties.content}}"

# Task Steps
1. Analyze the email content and determine its main intent.
2.Classify the email into one of the predefined categories.`,
        [DecisionAIFormItemKeys.variants]: [
            {
                id: 1,
                name: "Application Question",
                description:
                    "Contact is inquiring about their insurance application. This includes general questions, making changes to forms, or clarifying requirements.",
            },
            {
                id: 2,
                name: "Billing Issue",
                description:
                    "Contact has billing-related concerns, such as modifying or canceling a subscription, updating payment details, disputing charges, or inquiring about payment frequency.",
            },
            {
                id: 3,
                name: "Insurance Claim",
                description:
                    "Contact is reaching out to file a new claim, check the status of an existing claim, or dispute a claim decision.",
            },
            {
                id: 4,
                name: "Other",
                description:
                    "The email does not fit into the above categories. This may include account login issues, administrative concerns, or unrelated inquiries. Only choose this if no other category applies.",
            },
        ],
    },
    [AiDecision.LANGUAGE_DETECTION]: {
        [DecisionAIFormItemKeys.name]: "Split Language Team",
        [DecisionAIFormItemKeys.prompt]: `**TEMPLATE PROMPT** (modify prompt and output details for your use case, and delete this sentence)

### Background
You are a language classification AI. Your task is to detect the language used in a contact’s SMS response.

#Input
- Contact’s SMS content: "{{event.properties.content}}"

# Task Steps
1. Identify the primary language used.`,
        [DecisionAIFormItemKeys.variants]: [
            {
                id: 1,
                name: "English",
                description: "Customer message is primarily in English",
            },
            {
                id: 2,
                name: "Spanish",
                description: "Customer message is primarily in Spanish",
            },
            {
                id: 3,
                name: "Mandarin",
                description: "Customer message is primarily in Mandarin",
            },
            {
                id: 4,
                name: "Other",
                description: "All other languages.",
            },
        ],
    },
    [AiDecision.YES_OR_NO]: {
        [DecisionAIFormItemKeys.name]: "Qualify Lead by Zipcode",
        [DecisionAIFormItemKeys.prompt]: `**TEMPLATE PROMPT** (modify prompt and output details for your use case, and delete this sentence)

### Background
You are a lead qualification AI. Your task is to determine if a contact is qualified based on their zip code.

#Input
- Contact’s zipcode: "{{contact.customProperties.address.zipcode}}"

# Task Steps
1. Check if the zip code belongs to one of the five New York City boroughs: Manhattan, Brooklyn, Queens, The Bronx, or Staten Island.
2.If the zip code matches any borough, classify the contact as Qualified. If it does not, classify the contact as Not Qualified.
`,
        [DecisionAIFormItemKeys.variants]: [
            {
                id: 1,
                name: "Yes - Qualified",
                description: "Contact lives in any NYC borough based on zipcode.",
            },
            {
                id: 2,
                name: "No - Disqualified",
                description: "Contact does not live in any NYC borough based on zipcode.",
            },
        ],
    },
    [AiDecision.CUSTOM]: {
        [DecisionAIFormItemKeys.name]: "",
        [DecisionAIFormItemKeys.prompt]: "",
        [DecisionAIFormItemKeys.variants]: [
            {
                id: 1,
                name: "Output 1",
                description: "",
            },
            {
                id: 2,
                name: "Output 2",
                description: "",
            },
        ],
    },
};

const OUTPUT_RULES: FormListProps["rules"] = [
    {
        validator: async (_, outputs: { attribute: string; value: string }[]) => {
            const outputCount = outputs?.length ?? 0;
            if (outputCount < 2) {
                return Promise.reject(new Error("At least 2 outputs are required"));
            }
        },
        validateTrigger: ["onSubmit"],
        // badly exported types that hide "validateTrigger" property
        // see https://ant.design/components/form#rule
    } as NonNullable<FormListProps["rules"]>[number],
];

const getFormItemEmptyMessage = ({ title, items }: { title: string; items: string[] }) => (
    <div>
        <Empty
            data-testid="form-item-empty-state-message"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={title}
            className={styles.emptyIcon}
        />
        {items.map((item) => (
            <p key={item} className={styles.emptyText}>
                {item}
            </p>
        ))}
    </div>
);

const PROMPT_EMPTY_STATE = getFormItemEmptyMessage({
    title: "Select AI Decision Type to start. Best Practices",
    items: [
        "Provide background on the task upfront.",
        "Define dynamic variable with quote delimiters.",
        "Give clear instructions and break down complex task into multiple steps.",
    ],
});

const OUTPUT_DETAILS_EMPTY_STATE = getFormItemEmptyMessage({
    title: "Best Practices",
    items: [
        "Give mutually exclusive, collectively exhaustive output options.",
        "Add descriptions and provide examples to enhance the prompt.",
    ],
});

const MAX_OUTPUT_VARIANTS_COUNT = 10;

export function DecisionAIFormItems(): JSX.Element {
    const form = Form.useFormInstance();
    const dispatch = useDispatch();
    const activePanels = Array.from({ length: MAX_OUTPUT_VARIANTS_COUNT }, (_, i) => i);
    const [overriddenAt, setOverriddenAt] = useState(Date.now());

    const decisionType = Form.useWatch<AiDecisionType | undefined>(DecisionAIFormItemKeys.decisionType, form);

    const isEmptyState = !decisionType;

    const onDecisionTypeChanges = useCallback(
        (type: string) => {
            if (!(AI_DECISION_TYPES as string[]).includes(type)) {
                return;
            }
            form.setFieldsValue(DEFAULT_FORM_VALUES_PER_DECISION_TYPE[type as AiDecisionType]);
            dispatch(setIfShouldDropOutboundEdges(true));
            setOverriddenAt(Date.now());
        },
        [form]
    );

    const prompt = Form.useWatch(DecisionAIFormItemKeys.prompt, form);
    const model = Form.useWatch<OpenAIModels>(DecisionAIFormItemKeys.model, form);
    const variants = Form.useWatch<Array<DecisionAiVariant>>(DecisionAIFormItemKeys.variants, form);

    return (
        <>
            <Typography.Text className={styles.infoLine}>
                AI will select one of the output options based on the prompt input and descriptions. See best practices
                for prompting:&nbsp;
                <RVLearnMoreLink href="https://support.regal.ai/hc/en-us/articles/34916670227227-AI-Decision-Node-in-Journey-Builder" />
                .
            </Typography.Text>
            <RVFormFloatingLabelWrap
                showFormLabel
                name={DecisionAIFormItemKeys.decisionType}
                label="AI Decision Type"
                formItemHeader="AI Decision Details"
                required={true}
                rules={generateFormItemRequiredRules("AI Decision Type")}
            >
                <Select size="large" onChange={onDecisionTypeChanges} options={DECISION_TYPE_OPTIONS} />
            </RVFormFloatingLabelWrap>

            <RVFormFloatingLabelWrap
                name={DecisionAIFormItemKeys.name}
                placeholder="Name of AI Decision"
                required={true}
                rules={generateFormItemRequiredRules("Name of AI Decision")}
            >
                <Input className={sharedStyles.grayBorder} />
            </RVFormFloatingLabelWrap>

            <RVFormFloatingLabelWrap
                name={DecisionAIFormItemKeys.model}
                placeholder="Model"
                initialValue={OpenAIModels.GPT_4_MINI}
            >
                <Select size="large" options={MODEL_OPTIONS} />
            </RVFormFloatingLabelWrap>

            <DecisionAILexicalInput
                name={DecisionAIFormItemKeys.prompt}
                rules={generateFormItemRequiredRules("Prompt")}
                overriddenAt={overriddenAt}
                prompt={prompt}
                model={model}
                variants={variants}
                formItemHeaderTooltip={
                    <>
                        Provide background and dynamic variable definitions upfront. Give clear instructions and break
                        down complex task into multiple steps.&nbsp;
                        <RVLearnMoreLink
                            href="https://support.regal.ai/hc/en-us/articles/34916670227227-AI-Decision-Node-in-Journey-Builder"
                            className={styles.tooltipLearnMoreLink}
                        />
                        .
                    </>
                }
                emptyState={PROMPT_EMPTY_STATE}
                showEmptyState={isEmptyState}
            />

            <RVFormFloatingLabelWrap
                showFormLabel
                disablePlaceholder
                validateTrigger={["onSubmit"]}
                required
                formItemHeader="Output Details"
                formItemHeaderTooltip={
                    <>
                        Give mutually exclusive, collectively exhaustive output options. Add descriptions and provide
                        examples to enhance the prompt. Max 10. &nbsp;
                        <RVLearnMoreLink
                            href="https://support.regal.ai/hc/en-us/articles/34916670227227-AI-Decision-Node-in-Journey-Builder"
                            className={styles.tooltipLearnMoreLink}
                        />
                        .
                    </>
                }
            >
                {isEmptyState ? (
                    OUTPUT_DETAILS_EMPTY_STATE
                ) : (
                    <Form.List name={DecisionAIFormItemKeys.variants} rules={OUTPUT_RULES}>
                        {(fieldList, { add, remove }, { errors }) => (
                            <>
                                {!!fieldList.length && (
                                    <RVCollapse
                                        panelsList={fieldList.map((field, index: number) => ({
                                            id: index,
                                            forceRender: true,
                                            header: (
                                                <RVEditableFormItemPanelHeader
                                                    key={`outputPanel-${field.name}-header`}
                                                    noIndex
                                                    panelIndex={index}
                                                    form={form}
                                                    remove={() => {
                                                        remove(field.name);
                                                        dispatch(setIfShouldDropOutboundEdges(true));
                                                    }}
                                                    hideConditionRemove={fieldList.length <= 2}
                                                    formItemName={[field.name, DecisionAIVariantFormItemKeys.name]}
                                                />
                                            ),
                                            content: (
                                                <RVFormFloatingLabelWrap
                                                    key={`outputPanel-${field.name}-content`}
                                                    name={[field.name, DecisionAIVariantFormItemKeys.description]}
                                                    rules={generateFormItemRequiredRules("Output Description")}
                                                    label="Description"
                                                >
                                                    <Input.TextArea
                                                        autoSize={{ minRows: 1, maxRows: 3 }}
                                                        className={sharedStyles.grayBorder}
                                                    />
                                                </RVFormFloatingLabelWrap>
                                            ),
                                        }))}
                                        activeKey={activePanels}
                                    />
                                )}
                                <RVButton
                                    onClick={() => {
                                        const maxId = variants.reduce(
                                            (maxId, variant) => (variant.id > maxId ? variant.id : maxId),
                                            0
                                        );
                                        const splitId = (maxId ?? 0) + 1;
                                        add({
                                            id: splitId,
                                            name: `Output ${splitId}`,
                                            description: "",
                                        } as DecisionAiVariant);
                                    }}
                                    disabled={fieldList.length >= MAX_OUTPUT_VARIANTS_COUNT}
                                    icon={<PlusCircleOutlined />}
                                >
                                    Add Option
                                </RVButton>
                                <Form.ErrorList errors={errors} />
                            </>
                        )}
                    </Form.List>
                )}
            </RVFormFloatingLabelWrap>
        </>
    );
}
